import React, { useState, useEffect } from 'react';

import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import InteractiveGlowContainer from '../../../components/InteractiveGlowContainer';
import GradientText from '../../../components/GradientText';

const HeroSection = () => {
  const [visitorSource, setVisitorSource] = useState('direct');

  useEffect(() => {
    // Detect visitor source from referrer or URL parameters
    const referrer = document.referrer.toLowerCase();
    const urlParams = new URLSearchParams(window.location.search);
    const source = urlParams.get('source');

    if (source === 'gcp' || referrer.includes('cloud.google.com')) {
      setVisitorSource('gcp');
    } else if (referrer.includes('nvidia.com')) {
      setVisitorSource('nvidia');
    } else {
      setVisitorSource('direct');
    }
  }, []);

  const getHeroContent = () => {
    switch (visitorSource) {
      case 'gcp':
        return {
          badge: 'Google Cloud Partner',
          headline: 'Enterprise-Grade AI Services with Secure On-Prem & Cloud Deployments',
          subheading: 'AI Solutions for Smarter Businesses - Custom AI development, infrastructure, model fine-tuning, and secure deployments on GCP'
        };
      case 'nvidia':
        return {
          badge: 'NVIDIA Inception Partner',
          headline: 'Enterprise-Grade AI Services with Secure On-Prem & Cloud Deployments',
          subheading: 'GPU-accelerated AI solutions with custom RAG systems, HPC deployments, and enterprise security'
        };
      default:
        return {
          badge: 'AI Solutions for Smarter Businesses',
          headline: 'Enterprise-Grade AI Services with Secure On-Prem & Cloud Deployments',
          subheading: 'Custom AI consulting, deployment, infrastructure, model fine-tuning, and secure on-premise & cloud solutions'
        };
    }
  };

  const heroContent = getHeroContent();

  return (
    <section className="relative text-white overflow-hidden bg-transparent">
      {/* Semi-transparent overlay to ensure text readability */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900/80 via-blue-900/70 to-slate-800/80 backdrop-blur-sm"></div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-md rounded-full px-6 py-3 text-sm font-medium border border-white/30">
              <Icon name="Shield" size={16} className="text-blue-300" />
              <span className="text-blue-100">{heroContent.badge}</span>
            </div>

            {/* Headline */}
            <h1 className="text-4xl lg:text-6xl font-bold leading-tight">
              <GradientText
                colors={["#bae6fd", "#60a5fa", "#3b82f6", "#60a5fa", "#bae6fd"]}
                animationSpeed={12}
                className="block"
              >
                {heroContent.headline}
              </GradientText>
            </h1>

            {/* Subheading */}
            <p className="text-xl lg:text-2xl text-blue-100/90 leading-relaxed font-light">
              {heroContent.subheading}
            </p>

            {/* Credibility badges trimmed to avoid unsupported claims */}
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20">
                <Icon name="Shield" size={16} className="text-blue-300" />
                <span className="text-white/90 font-medium">Enterprise Security</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                size="lg"
                className="border-white/30 text-white hover:bg-white/10 backdrop-blur-sm"
                onClick={() => window.location.href = '/enterprise-ai-solutions-service-architecture'}
              >
                <Icon name="ArrowRight" size={20} className="mr-2" />
                View Services
              </Button>
            </div>
          </div>

          {/* Visual - Partner/stack badges (numbers removed) */}
          <div className="relative">
            <InteractiveGlowContainer>
              <div className="space-y-8">
                {/* Playbook tiles (aligned with About story) */}
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl p-6 text-center border border-blue-400/30">
                    <div className="text-2xl font-bold text-white mb-2">Outcome‑First Delivery</div>
                    <div className="text-sm text-blue-200 font-medium">START WITH THE RESULT</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl p-6 text-center border border-blue-400/30">
                    <div className="text-2xl font-bold text-white mb-2">Safety & Auditability</div>
                    <div className="text-sm text-blue-200 font-medium">GOVERNANCE BY DESIGN</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl p-6 text-center border border-blue-400/30">
                    <div className="text-2xl font-bold text-white mb-2">Data Sovereignty</div>
                    <div className="text-sm text-blue-200 font-medium">ON‑PREM/VPC OPTIONS</div>
                  </div>
                  <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-xl p-6 text-center border border-blue-400/30">
                    <div className="text-2xl font-bold text-white mb-2">Rigorous Telemetry</div>
                    <div className="text-sm text-blue-200 font-medium">OBSERVABILITY & EVALS</div>
                  </div>
                </div>

                {/* Feature Badges */}
                <div className="flex flex-wrap justify-center gap-3 mt-6">
                  <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-xs border border-white/20">
                    <Icon name="Cloud" size={14} className="text-blue-300" />
                    <span className="text-white/90">Built for Google Cloud (Vertex AI)</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-xs border border-white/20">
                    <Icon name="Cpu" size={14} className="text-blue-300" />
                    <span className="text-white/90">On-Prem GPU (NVIDIA Triton)</span>
                  </div>
                  <div className="flex items-center space-x-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-xs border border-white/20">
                    <Icon name="Shield" size={14} className="text-blue-300" />
                    <span className="text-white/90">SOC2-minded MLOps & Governance</span>
                  </div>
                </div>
              </div>
            </InteractiveGlowContainer>

            {/* Decorative floating elements retained */}
            <div className="absolute -top-4 -right-4 bg-blue-600 rounded-full p-3 shadow-lg">
              <Icon name="Layers" size={24} className="text-white" />
            </div>
            <div className="absolute -bottom-4 -left-4 bg-blue-500 rounded-full p-3 shadow-lg">
              <Icon name="Server" size={24} className="text-white" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;